import React, { Children, useCallback, isValidElement } from 'react';
import { useForm, Controller } from 'react-hook-form';

import WpTextField from '../text-field/WpTextField';
import WpSelect from '../select/WpSelect';
import WpUserInput from '../user-input/WpUserInput';
import WpRadioGroup from '../radio/WpRadioGroup';
import WpSelectV2 from '../select/WpSelectV2';
import WpDateInput from '../date-input/WpDateInput';
import WpTimeInput from '../time-input/WpTimeInput';
import WpCheckbox from '../checkbox/WpCheckbox';

import { isElementOfType, sanitizeFragments } from '../../utilities/components';

interface Validator {
  callback: Function;
  error: string;
}

interface Validators {
  [key: string]: Validator;
}

export interface FormProps {
  /** Grants the browser the ability to autocomplete input elements */
  autoComplete?: 'on' | 'off';
  /** The content to display inside the form. */
  children?: React.ReactNode;
  /**
   * Callback when the form is submitted. This will be passed in the form data
   * as an object where each key is the id of each form element and the value
   * is the value of the form element.
   */
  onSubmit?: (data: { [formElementId: string]: any }) => void;
  method?: 'post' | 'put';
  action?: string;
  id?: string;
}

/**
 * A component that wraps form elements and provides validation. This was
 * made to ease the use of the react-hook-form library and provide a nice
 * wrapper for our forms.
 */
const WpForm = ({
  // autoComplete = 'off',
  children,
  onSubmit,
  method,
  action,
  id,
}: FormProps) => {
  const { control, handleSubmit } = useForm();

  const handleSubmitAction = useCallback(
    data => {
      if (onSubmit) onSubmit(data);

      if (action && method) {
        // (josh)  This is kinda janky, but allows us to
        // to use our WpForm, and it's validation logic
        // with traditional html form submitting
        const form = document.createElement('form');
        form.action = action;
        form.method = method;
        form.style.visibility = 'hidden';
        form.style.position = 'absolute';
        form.style.top = '-1000px';
        form.style.left = '-1000px';

        const addInput = (name, value) => {
          const input = document.createElement('input');
          // extra check to keep password masked
          input.type = name.includes('password') ? 'password' : 'text';
          input.name = name;
          input.value = value as string;
          form.appendChild(input);
        };

        Object.entries(data).map(([key, val]) => {
          const valTypeOf = typeof val;
          if (valTypeOf === 'string' || valTypeOf === 'number' || valTypeOf === 'boolean') {
            addInput(key, val);
            // react-hook-forms auto nests objecs, aka name[email]
            // becomes name: {email: xxxx}
          } else if (valTypeOf === 'object') {
            Object.entries(val).map(([nestedKey, nestedVal]) => {
              const nestedName = `${key}[${nestedKey}]`;
              addInput(nestedName, nestedVal);
            });
          }
        });

        document.body.appendChild(form);
        form.submit();
        setTimeout(() => document.body.removeChild(form), 1000);
      }
    },
    [onSubmit, action, method]
  );

  const getValidators = (validators: Validators) => {
    if (!validators) return null;

    let v = {};

    for (const [key, value] of Object.entries(validators)) {
      v = { ...v, [key]: value.callback };
    }
    return v;
  };

  const getErrorMessage = ({ label, error, validate, meta }) => {
    if (!error) {
      return null;
    }
    // if custom validation function pull the error message from validation prop
    // if (validate && validate.hasOwnProperty(error.type)) {
    if (validate && Object.prototype.hasOwnProperty.call(validate, error.type)) {
      return validate[error.type].error;
    }

    switch (error.type) {
      case 'required':
        return `${label} is required`;
      case 'minLength':
        return `${label} must be at least ${meta.minLength} characters`;
      case 'min':
        return `${label} must be less than ${meta.min}`;
      case 'max':
        return `${label} cannot be more than ${meta.max}`;
      default:
        return null;
    }
  };

  const checkChildrenForFormElements = childrenToRender => {
    const sanitizedChildren = childrenToRender && sanitizeFragments(childrenToRender);

    return Children.map(sanitizedChildren, (child: React.ReactElement<any>) => {
      const isValid = isValidElement(child);
      if (
        child &&
        isElementOfType(child, [WpTextField, WpSelect, WpDateInput, WpRadioGroup, WpSelectV2, WpUserInput, WpTimeInput])
      ) {
        return (
          <Controller
            name={child.props.id}
            control={control}
            // (josh) this default value might need to change in the future after
            // we introduce other form input types
            defaultValue={child.props.value || child.props.defaultValue || ''}
            render={({ field, fieldState }) => {
              return React.cloneElement(child, {
                ...field,
                inputRef: field.ref,
                error: getErrorMessage({
                  label: child.props.label,
                  error: fieldState.error,
                  validate: child.props.validate && child.props.validate,
                  meta: {
                    minLength: child.props.minLength,
                    min: child.props.min,
                    max: child.props.max,
                  },
                }),
                value: field.value,
              });
            }}
            rules={{
              required: child.props.required,
              minLength: child.props.minLength,
              validate: child.props.validate && getValidators(child.props.validate),
            }}
          />
        );
      } else if (child && isElementOfType(child, [WpCheckbox])) {
        return (
          <Controller
            name={child.props.id}
            control={control}
            defaultValue={child.props.checked || false}
            render={({ field, fieldState }) => {
              return React.cloneElement(child, {
                inputRef: field.ref,
                error: getErrorMessage({
                  label: child.props.label,
                  error: fieldState.error,
                  validate: child.props.validate && child.props.validate,
                  meta: null,
                }),
                ...field,
              });
            }}
            rules={{
              required: child.props.required,
              validate: child.props.validate && getValidators(child.props.validate),
            }}
          />
        );
      } else if (isValid && child.props) {
        return React.cloneElement(child, { children: checkChildrenForFormElements(child.props.children) });
      } else {
        return child;
      }
    });
  };
  const idProps = id ? { id } : {};
  return (
    <form {...idProps} className="WpForm" onSubmit={handleSubmit(handleSubmitAction)}>
      {checkChildrenForFormElements(children)}
    </form>
  );
};

export default WpForm;
